<template>
  <div class="route-container" v-if="this.nextArrivalPoint">
    <div class="sea-route-figure">
      <svg v-if="isStartRoute" width="100" height="40" viewBox="0 0 100 100">
        <defs>
          <g id="sea-route-start-line">
            <line
              x1="80"
              y1="15"
              x2="200"
              y2="15"
              :stroke="routeStyle.rightLine.color"
              :stroke-dasharray="routeStyle.rightLine.dash"
              :stroke-width="routeStyle.rightLine.width"
            />
          </g>
          <g id="sea-route-point">
            <rect
              x="50"
              y="40"
              width="30"
              height="30"
              :fill="routeStyle.pointColor"
              class="cursor-pointer"
              @click="onPointClick"
              @mouseover="mouseOver"
              @mouseleave="mouseleave"
            />
            <use
              xlink:href="#sea-route-start-line"
              transform="translate(0, 40)"
            />
          </g>
        </defs>
        <use xlink:href="#sea-route-point" transform="translate(0, 20)"/>
      </svg>
      <svg
        v-if="isIntermediateRoute"
        width="100"
        height="40"
        viewBox="0 0 100 100"
      >
        <defs>
          <g :id="lineGroupId">
            <line
              x1="70"
              y1="15"
              x2="200"
              y2="15"
              :stroke="routeStyle.rightLine.color"
              :stroke-dasharray="routeStyle.rightLine.dash"
              :stroke-width="routeStyle.rightLine.width"
            />
            <line
              x1="-100"
              y1="15"
              :x2="arrowLine2X"
              y2="15"
              :stroke="routeStyle.leftLine.color"
              :stroke-dasharray="routeStyle.leftLine.dash"
              :stroke-width="routeStyle.leftLine.width"
            />
            <svg
              v-if="isNextArrivalPoint && !this.isLessThan1kn"
              width="40"
              height="35"
              viewBox="-5 0 38 35"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="
              M32.7544 16.9401
              L2.76222 29.8388
              C1.10167 30.5519 -0.5591 28.7692 0.181191 27.0495
              L4.90309 15.8495
              C5.12318 15.3041 5.12318 14.6959 4.90309 14.1506
              L0.181191 2.95072
              C-0.538927 1.23089 1.10167 -0.55187 2.76222 0.161232
              L32.7544 13.06
              C34.4152 13.7521 34.4152 16.227 32.7544 16.9401
              Z"
                :fill="this.sdfColor.LightGreen"
              />
            </svg>
          </g>
          <g :id="pointGroupId">
            <rect
              class="cursor-pointer"
              x="40"
              y="40"
              width="30"
              height="30"
              :fill="routeStyle.pointColor"
              @click="onPointClick"
              @mouseover="mouseOver"
              @mouseleave="mouseleave"
            />
            <use :xlink:href="lineGroupXlinkId" transform="translate(0, 40)"/>
          </g>
        </defs>
        <use :xlink:href="pointGroupXlinkId" transform="translate(0, 20)"/>
      </svg>
      <svg v-if="isEndRoute" width="100" height="40" viewBox="0 0 100 100">
        <defs>
          <g id="sea-route-end-line">
            <line
              x1="-100"
              y1="15"
              :x2="arrivalPointArrowLine2X"
              y2="15"
              :stroke="routeStyle.leftLine.color"
              :stroke-dasharray="routeStyle.leftLine.dash"
              :stroke-width="routeStyle.leftLine.width"
            />
            <svg
              v-if="isNextArrivalPoint && !this.isArrivedPoint"
              width="40"
              height="35"
              viewBox="0 0 30 35"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="
              M32.7544 16.9401
              L2.76222 29.8388
              C1.10167 30.5519 -0.5591 28.7692 0.181191 27.0495
              L4.90309 15.8495
              C5.12318 15.3041 5.12318 14.6959 4.90309 14.1506
              L0.181191 2.95072
              C-0.538927 1.23089 1.10167 -0.55187 2.76222 0.161232
              L32.7544 13.06
              C34.4152 13.7521 34.4152 16.227 32.7544 16.9401
              Z"
                :fill="this.sdfColor.LightGreen"
              />
            </svg>
          </g>
          <g id="sea-route-end-point">
            <rect
              class="cursor-pointer"
              x="40"
              y="40"
              width="30"
              height="30"
              :fill="routeStyle.pointColor"
              @click="onPointClick"
              @mouseover="mouseOver"
              @mouseleave="mouseleave"
            />
            <use
              xlink:href="#sea-route-end-line"
              transform="translate(0, 40)"
            />
          </g>
        </defs>
        <use
          xlink:href="#sea-route-end-point"
          transform="translate(0, 20)"
          class="cursor-pointer"
        />
      </svg>
    </div>
    <div class="sea-route-point-name">
      <p :class="routeNameTextClass">{{ routeName }}</p>
    </div>
    <sea-route-tooltip
      :id="tooltipId"
      :color="tooltip.color"
      :text="tooltip.text"
      class="sea-route-tooltip"
      ref="seaRouteTooltip"
      position="bottom"
    >
      <p></p>
    </sea-route-tooltip>
  </div>
</template>

<script>
import {
  ColorMixin,
  MappingTableMixin,
  StoreMonitoringVesselManeuveringMixin
} from "@/mixins/addon/secure-dataflow";

import SeaRouteTooltip from "../../tooltip/SeaRouteTooltip";

export default {
  name: "",
  mixins: [
    ColorMixin,
    MappingTableMixin,
    StoreMonitoringVesselManeuveringMixin
  ],
  components: {
    SeaRouteTooltip
  },
  props: {
    route: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      routeStyle: {
        pointColor: null,
        leftLine: {
          color: null,
          dash: "5 5",
          width: "5"
        },
        rightLine: {
          color: null,
          dash: "5 5",
          width: "5"
        }
      },
      isActive: false,
      tooltip: {
        color: null,
        text: null
      },
      isNoTooltip: false
    };
  },
  watch: {
    nextArrivalPoint(newVal, oldVal) {
      this.update();
    },
    isArrivedPoint(newVal, oldVal) {
      this.update();
    },
    isLessThan1kn(newVal, oldVal) {
      this.update();
    }
  },
  computed: {
    isStartRoute() {
      return this.route.properties.number_of_point === 0;
    },
    isIntermediateRoute() {
      return (
        this.route.properties.number_of_point !== 0 &&
        this.route.properties.number_of_point !==
        this.arrivalPoint.properties.number_of_point
      );
    },
    isNextArrivalPoint() {
      return (
        this.nextArrivalPoint.number_of_point ===
        this.route.properties.number_of_point
      );
    },
    isEndRoute() {
      return (
        this.route.properties.number_of_point ===
        this.arrivalPoint.properties.number_of_point
      );
    },
    lineGroupId() {
      return `sea-route-intermediate-line__${this.route.properties.number_of_point}`;
    },
    lineGroupXlinkId() {
      return `#sea-route-intermediate-line__${this.route.properties.number_of_point}`;
    },
    pointGroupId() {
      return `sea-route-intermediate-point__${this.route.properties.number_of_point}`;
    },
    pointGroupXlinkId() {
      return `#sea-route-intermediate-point__${this.route.properties.number_of_point}`;
    },
    tooltipId() {
      return `sea-route-tooltip__${this.route.properties.number_of_point}`;
    },
    arrowLine2X() {
      return this.nextArrivalPoint.number_of_point ===
      this.route.properties.number_of_point && !this.isLessThan1kn
        ? 30
        : 40;
    },
    arrivalPointArrowLine2X() {
      if (this.isArrivedPoint) {
        return 50;
      }
      if (
        this.nextArrivalPoint.number_of_point ===
        this.route.properties.number_of_point
      ) {
        return 30;
      }
      return 50;
    },
    routeName() {
      return this.route.name;
    },
    routeNameTextClass() {
      if (this.isArrivedPoint) return "sdf-text-14-400-light-green";
      return this.nextArrivalPoint.number_of_point <=
      this.route.properties.number_of_point
        ? "sdf-text-14-400-mouse-gray"
        : "sdf-text-14-400-light-green";
    }
  },
  methods: {
    update() {
      this.setTooltip();
      if (this.isArrivedPoint) {
        this.routeStyle.pointColor = this.sdfColor.LightGreen;
        this.setLeftLineGreenWidth3()
        this.setRightLineGreenWidth3()
      } else if (this.latestDeparturePoint === null) {
        this.routeStyle.pointColor = this.sdfColor.MouseGray;
        this.setLeftLineGray()
        this.setRightLineGray()
      } else if (
        this.nextArrivalPoint.number_of_point ===
        this.route.properties.number_of_point
      ) {
        this.routeStyle.pointColor = this.sdfColor.MouseGray;
        if (this.isLessThan1kn) {
          this.setLeftLineGray()
          this.setRightLineGray()
        } else {
          this.setLeftLineGreenWidth10()
          this.setRightLineGray()
        }
      } else if (
        this.latestDeparturePoint.number_of_point ===
        this.route.properties.number_of_point
      ) {
        this.routeStyle.pointColor = this.sdfColor.LightGreen;
        if (this.isLessThan1kn) {
          this.setLeftLineGreenWidth3()
          this.setRightLineGray()
        } else {
          this.routeStyle.pointColor = this.sdfColor.LightGreen;
          this.setLeftLineGreenWidth3()
          this.setRightLineGreenWidth10()
        }
      } else if (
        this.nextArrivalPoint.number_of_point <
        this.route.properties.number_of_point
      ) {
        this.routeStyle.pointColor = this.sdfColor.MouseGray;
        this.setLeftLineGray()
        this.setRightLineGray()
      } else {
        this.routeStyle.pointColor = this.sdfColor.LightGreen;
        this.setLeftLineGreenWidth3()
        this.setRightLineGreenWidth3()
      }
    },
    setLeftLineGreenWidth3() {
      this.routeStyle.leftLine.color = this.sdfColor.LightGreen;
      this.routeStyle.leftLine.dash = "";
      this.routeStyle.leftLine.width = "3";
    },
    setRightLineGreenWidth3() {
      this.routeStyle.rightLine.color = this.sdfColor.LightGreen;
      this.routeStyle.rightLine.dash = "";
      this.routeStyle.rightLine.width = "3";
    },
    setLeftLineGreenWidth10() {
      this.routeStyle.leftLine.color = this.sdfColor.LightGreen;
      this.routeStyle.leftLine.dash = "";
      this.routeStyle.leftLine.width = "10";
    },
    setRightLineGreenWidth10() {
      this.routeStyle.rightLine.color = this.sdfColor.LightGreen;
      this.routeStyle.rightLine.dash = "";
      this.routeStyle.rightLine.width = "10";
    },
    setRightLineGray() {
      this.routeStyle.rightLine.color = this.sdfColor.MouseGray;
      this.routeStyle.rightLine.dash = "5 5";
      this.routeStyle.rightLine.width = "3";
    },
    setLeftLineGray() {
      this.routeStyle.leftLine.color = this.sdfColor.MouseGray;
      this.routeStyle.leftLine.dash = "5 5";
      this.routeStyle.leftLine.width = "3";
    },
    onPointClick() {
      this.SET_JUMP_TO({
        center: [this.route.longitude, this.route.latitude],
        zoom: 14,
        routeNumber: this.route.properties.number_of_point
      });
    },
    mouseOver() {
      if (this.isNoTooltip) return;
      this.isActive = true;
      this.$refs.seaRouteTooltip.setActive(true);
    },
    mouseleave() {
      if (this.isNoTooltip) return;
      this.isActive = false;
      this.$refs.seaRouteTooltip.setActive(false);
    },
    setTooltip() {
      let info = this.getSeaRouteInfo(this.route.properties.number_of_point);
      if (typeof info === "undefined") {
        this.isNoTooltip = true;
        return;
      }
      this.isNoTooltip = false;
      if (info.isPassing) {
        this.tooltip.color = this.sdfColor.DarkYellowGreen;
        this.tooltip.text = `<ul class="sdf-text-14-400-white sea-route-tooltip-contents">
<li>到着目標日時：${info.passingEstimatedTime || "---"}</li>
<li>到着実績日時：${info.passingTime || "---"}</li>
</ul>`;
      } else {
        this.tooltipColor = this.sdfColor.Gray;
        let description;
        if (info.isTurnRate) {
          description = `<ul class="sdf-text-14-400-white sea-route-tooltip-contents">
<li>到着目標日時：${info.passingEstimatedTime || "---"}</li>
<li>運航モード：${info.operationMode || "---"}</li>
<li>変針方法：${info.veeringMethod || "---"}</li>
<li>ターンレート：${info.turnRate || "---"}</li>
</ul>`;
        } else if (info.isRadius) {
          description = `<ul class="sdf-text-14-400-white sea-route-tooltip-contents">
<li>到着目標日時：${info.passingEstimatedTime || "---"}</li>
<li>運航モード：${info.operationMode || "---"}</li>
<li>変針方法：${info.veeringMethod || "---"}</li>
<li>旋回径：${info.turnRadius || "---"}</li>
</ul>`;
        }
        this.tooltip.text = description;
      }
    }
  },
  mounted() {
    this.update();
  }
};
</script>

<style lang="scss"></style>
